import { extractBseApiError } from "./bseOrder";
import { detailFromGetUcc } from "./uccUpdateHelpers";

export { extractBseApiError };

const BANK_TYPE_MAP = { SB: "SB", SA: "SB", CA: "CA", CB: "CB" };

export function resolveMandateMode(mandateType, mode) {
  const type = String(mandateType || "U").trim().toUpperCase();
  if (type === "U") return "DD";
  if (type === "N" || type === "X") return "ACH";
  return String(mode || "ACH").trim().toUpperCase();
}

/** Working Postman for member 63164 uses SB; map SA→SB. */
export function mapBankAccountType(value) {
  const key = String(value || "SB").trim().toUpperCase();
  if (key === "SA") return "SB";
  return BANK_TYPE_MAP[key] || key;
}

const IST = "Asia/Kolkata";
/** Match BSE live rule: max 30 years ("END DATE EXCEEDS 30 YEARS OF MAXIMUM DURATION"). */
export const MAX_MANDATE_YEARS = 30;

export function bseIstToday() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: IST }).format(new Date());
}

function parseIsoDate(iso) {
  const s = String(iso || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  return s;
}

function addCalendarYearsIso(isoDate, years) {
  const start = parseIsoDate(isoDate);
  if (!start) return "";
  const [y, m, d] = start.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCFullYear(dt.getUTCFullYear() + years);
  return dt.toISOString().slice(0, 10);
}

function addCalendarDaysIso(isoDate, days) {
  const start = parseIsoDate(isoDate);
  if (!start) return "";
  const [y, m, d] = start.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

export function maxValidTillFromStart(startDate) {
  const plusYears = addCalendarYearsIso(startDate, MAX_MANDATE_YEARS);
  if (!plusYears) return "";
  return addCalendarDaysIso(plusYears, -1);
}

export function defaultValidTill(startDate) {
  const start = String(startDate || bseIstToday()).trim();
  return maxValidTillFromStart(start) || maxValidTillFromStart(bseIstToday());
}

function maxIsoDate(a, b) {
  if (!a) return b || "";
  if (!b) return a;
  return a >= b ? a : b;
}

/**
 * Build /mandate_register body per BSE portal + working Postman (member 63164).
 * Default request_type = REGISTRATION (ENTRY only for excluded member).
 */
export function buildMandateRegisterPayload(form = {}) {
  const ucc = String(form.ucc || form.client_code || "").trim();
  const member = String(form.member || process.env.REACT_APP_BSE_MEMBER_ID || "63164").trim();
  const today = bseIstToday();
  const startDate = maxIsoDate(String(form.start_date || "").trim(), today);
  const validTill = defaultValidTill(startDate);

  const requestTypeRaw = String(form.request_type || "REGISTRATION").trim().toUpperCase();
  const requestType = requestTypeRaw === "ENTRY" ? "ENTRY" : "REGISTRATION";
  const mandateType = String(form.mandate_type || "U").trim().toUpperCase();

  const data = {
    investor: { ucc },
    member,
    investor_bank_details: {
      ifsc: String(form.ifsc || form.ifsc_code || "").trim().toUpperCase(),
      no: String(form.account_no || form.bank_acc_num || "").trim(),
      type: mapBankAccountType(form.bank_type || form.bank_acc_type),
      name: String(form.bank_name || "").trim(),
      branch: String(form.bank_branch || "").trim(),
    },
    amount: Number(form.amount),
    start_date: startDate,
    valid_till: validTill,
    // Postman sample: reg_date matches start_date for registration
    reg_date: startDate,
    type: mandateType,
    mode: resolveMandateMode(mandateType, form.mode),
    frequency: "AS AND WHEN PRESENTED",
    request_type: requestType,
    redirect_url: String(form.redirect_url || "").trim(),
  };

  data.mem_details = {
    euin: String(form.euin || "").trim(),
    euin_flag: form.euin_flag !== undefined ? !!form.euin_flag : true,
    sub_br_code: String(form.sub_br_code ?? "").trim(),
    sub_br_arn: String(form.sub_br_arn || "").trim(),
  };
  const partner = String(form.partner_id || "").trim();
  if (partner) data.mem_details.partner_id = partner;

  if (mandateType === "U") {
    const vpaRaw = form.vpa || form.upi_vpa;
    const vpaList = Array.isArray(vpaRaw)
      ? vpaRaw.map((v) => String(v).trim()).filter(Boolean)
      : String(vpaRaw || "")
          .split(/[,;\s]+/)
          .map((v) => v.trim())
          .filter(Boolean);
    if (vpaList.length) data.investor_bank_details.vpa = vpaList;
  }

  // Portal: mem_mandate_info only for ENTRY (excluded member)
  if (requestType === "ENTRY") {
    data.mem_mandate_info = {
      member_mandate_id: String(form.member_mandate_id || "").trim(),
      mandate_status_date: String(
        form.mandate_status_date || new Date().toISOString()
      ).trim(),
      umrn_number: String(form.umrn_number || "").trim(),
      utility_code: String(form.utility_code || "").trim(),
      sponsor_code: String(form.sponsor_code || "").trim(),
    };
  }

  return { data };
}

export function buildMandateListPayload({ memberId, clientCode, start = 0, length = 100 } = {}) {
  const filter = {
    member_code: String(memberId || process.env.REACT_APP_BSE_MEMBER_ID || "63164").trim(),
  };
  const ucc = String(clientCode || "").trim();
  if (ucc) filter.investor = { ucc };
  return {
    data: {
      fields: ["ALL"],
      start,
      length,
      count_only: false,
      filter_param: filter,
    },
  };
}

export function buildMandateGetPayload(mandateId) {
  return { data: { id: mandateId, exch_mandate_id: mandateId } };
}

export function buildMandateCancelPayload(mandateId, ucc = "") {
  const data = { ids: [mandateId] };
  const code = String(ucc || "").trim();
  if (code) data.investor = { ucc: code };
  return { data };
}

const MANDATE_TYPE_LABELS = { N: "eNACH", X: "NACH", U: "UPI AutoPay" };

export function mapBseMandateListItem(item, index = 0) {
  const row = item && typeof item === "object" ? item : {};
  const bank = row.investor_bank_details || {};
  const memInfo = row.mem_mandate_info || {};
  const type = String(row.type || "").toUpperCase();
  return {
    id: row.exch_mandate_id || row.id || index + 1,
    exchMandateId: row.exch_mandate_id || row.id || "",
    name: bank.name || row.investor?.name || "-",
    ucc: row.investor?.ucc || "-",
    arn: row.mem_details?.sub_br_arn || "",
    account: bank.no || "-",
    bank: bank.name || "-",
    startDate: row.start_date || "-",
    endDate: row.valid_till || "-",
    umrn: memInfo.umrn_number || row.umrn || "",
    amount: row.amount ?? "",
    mandateType: MANDATE_TYPE_LABELS[type] || type || "-",
    status: String(row.mandate_status || row.status || "pending").toLowerCase(),
    raw: row,
  };
}

export function parseMandateListResponse(body) {
  const lists = body?.data?.lists;
  if (Array.isArray(lists)) return lists.map((item, i) => mapBseMandateListItem(item, i));
  const items = body?.data?.mandates;
  if (Array.isArray(items)) return items.map((item, i) => mapBseMandateListItem(item, i));
  return [];
}

export function mapUccDetailToMandateForm(detail) {
  const d = detail || {};
  const name = [d.first_name, d.middle_name, d.last_name].filter(Boolean).join(" ").trim();
  return {
    ifsc: d.ifsc_code || "",
    account_no: d.bank_acc_num || "",
    bank_type: mapBankAccountType(d.bank_acc_type || "SB"),
    bank_name: name,
  };
}

export function parseUccForMandateBank(body) {
  return mapUccDetailToMandateForm(detailFromGetUcc(body));
}

export function getMandateRegistrationLink(body) {
  const data = body?.data;
  if (!data) return "";
  if (typeof data.link === "string" && data.link) return data.link;
  if (data.link?.url) return data.link.url;
  return "";
}

/** Humanize BSE mandate errors (agency / Entry / dates). */
export function formatMandateRegisterError(err) {
  const base = extractBseApiError(err);
  const lower = String(base || "").toLowerCase();
  const hints = [];
  if (lower.includes("agency_code") || lower.includes("no agency found")) {
    hints.push(
      "BSE has no mandate agency configured for this member + type. Ask BSE to enable agency for type U (UPI), N (eNACH), or X (NACH) on member 63164 — or try another type."
    );
  }
  if (lower.includes("mem_mandate_info") || lower.includes("request type entry")) {
    hints.push(
      "Use request_type REGISTRATION for normal mandates. ENTRY is only for excluded members and requires mem_mandate_info."
    );
  }
  if (lower.includes("invalid_date") || lower.includes("30 years")) {
    hints.push(
      "BSE max mandate duration is 30 years. Use valid_till ≤ start_date + 30 years − 1 day (e.g. 2026-07-28 → 2056-07-27)."
    );
  }
  if (!hints.length) return base;
  return `${base}<br/><br/><small class="text-muted">${hints.join("<br/>")}</small>`;
}
